/**
 * useTableFilters variant for server-side paginated tables.
 *
 * Every filter mutation (change, clear, clear-all, date range update) also
 * resets pagination to the first page, since any filter change invalidates
 * the current page offset.
 *
 * Used by: ProbandsSamplesPage (sample search), Worklist
 *
 * @example
 * const resetPage = useCallback(() => setCurrentPage(1), []);
 * const {
 *   columnFilters,
 *   handleFilterChange, // already resets the page
 *   handleFilterClear,
 *   handleClearAllFilters,
 *   getActiveFiltersCount,
 * } = usePagedTableFilters({ initialFilters }, resetPage);
 */
import { useCallback } from 'react';

import type {
  UseTableFiltersOptions,
  UseTableFiltersReturn,
} from './useTableFilters';
import { useTableFilters } from './useTableFilters';

export const usePagedTableFilters = (
  options: UseTableFiltersOptions = {},
  resetPage: () => void,
): UseTableFiltersReturn => {
  const filters = useTableFilters(options);

  const handleFilterChange = useCallback(
    (key: string, values: string[]) => {
      filters.handleFilterChange(key, values);
      resetPage();
    },
    [filters.handleFilterChange, resetPage],
  );

  const handleFilterClear = useCallback(
    (key: string) => {
      filters.handleFilterClear(key);
      resetPage();
    },
    [filters.handleFilterClear, resetPage],
  );

  const handleClearAllFilters = useCallback(() => {
    filters.handleClearAllFilters();
    resetPage();
  }, [filters.handleClearAllFilters, resetPage]);

  const setDateFrom = useCallback(
    (value: string) => {
      filters.setDateFrom(value);
      resetPage();
    },
    [filters.setDateFrom, resetPage],
  );

  const setDateTo = useCallback(
    (value: string) => {
      filters.setDateTo(value);
      resetPage();
    },
    [filters.setDateTo, resetPage],
  );

  return {
    ...filters,
    setDateFrom,
    setDateTo,
    handleFilterChange,
    handleFilterClear,
    handleClearAllFilters,
  };
};
